import { useMemo, useCallback, type MouseEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { useTranslation } from 'react-i18next';
import { useTargetBrandConfig } from 'FrontRoyalAngular/useTargetBrandConfig';
import { useCurrentUser, useDateHelper, useNavigationHelper } from 'FrontRoyalAngular';
import { getAdmissionOffer, getCohort } from 'Users';
import { getApplicableAdmissionRound, getRelativeDate } from 'Cohorts';
import { offeredAdmission, getRegistrationDeadline } from 'AdmissionOffer';
import { getProgramConfigValue } from 'Program';
import '../locales/studentNetworkV2/studentNetworkV2-en.json';

type PartialAccessModalProps = {
    onClose: () => void;
};

const PRIMARY_BTN =
    'mx-auto flex h-10 items-center justify-center rounded-5 bg-primary px-6 text-base font-semibold text-white transition-colors hover:bg-primary/90';

type PartialAccessModalPresentationProps = {
    brandName: string;
    cta: { type: 'apply' | 'register'; date: string } | null;
    onClose: () => void;
    onCtaClick: () => void;
};

export function PartialAccessModalPresentation({
    brandName,
    cta,
    onClose,
    onCtaClick,
}: PartialAccessModalPresentationProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.partialAccessModal' });

    const handleBackdropClick = useCallback(
        (e: MouseEvent) => {
            if (e.target === e.currentTarget) onClose();
        },
        [onClose],
    );

    return (
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop dismiss
        <div
            className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-5"
            onClick={handleBackdropClick}
        >
            <div className="relative w-full max-w-[420px] rounded-8 bg-white p-7.5 shadow-[0_4px_20px_0_rgba(0,0,0,0.2)] md:max-w-[600px]">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute end-4 top-4 text-lg text-slate-grey hover:text-black"
                    aria-label="Close"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
                <p className="mb-3 text-center text-lg text-black">{t('explain', { brandName })}</p>
                <p className="mb-5 text-center text-base text-slate-grey">{t('instructions')}</p>
                {cta ? (
                    <div className="text-center">
                        <p className="mb-3 text-base text-slate-grey">
                            {t(cta.type === 'register' ? 'registrationDeadline' : 'deadline')}{' '}
                            <span className="font-semibold text-black">{cta.date}</span>
                        </p>
                        <button type="button" onClick={onCtaClick} className={PRIMARY_BTN}>
                            {t(cta.type === 'register' ? 'registerNow' : 'applyNow')}
                        </button>
                    </div>
                ) : (
                    <button type="button" onClick={onClose} className={PRIMARY_BTN}>
                        {t('explore')}
                    </button>
                )}
            </div>
        </div>
    );
}

// oxlint-disable-next-line max-lines-per-function -- single modal with two CTA variants
export function PartialAccessModal({ onClose }: PartialAccessModalProps) {
    const { brandNameShort: brandName } = useTargetBrandConfig();
    const currentUser = useCurrentUser();
    const dateHelper = useDateHelper();
    const { loadRoute } = useNavigationHelper();

    const cta = useMemo(() => {
        const cohort = getCohort(currentUser);
        if (!cohort) return null;
        if (!getProgramConfigValue(cohort.programType, 'isDegreeProgram')) return null;

        const admissionOffer = getAdmissionOffer(currentUser);
        if (admissionOffer && offeredAdmission(admissionOffer)) {
            const regDeadline = getRegistrationDeadline(admissionOffer, cohort);
            if (regDeadline) {
                return {
                    type: 'register' as const,
                    date: dateHelper.formattedUserFacingMonthDayLong(regDeadline),
                };
            }
        }

        if (currentUser?.hasEverApplied) return null;

        const round = getApplicableAdmissionRound(cohort);
        if (!round) return null;
        return {
            type: 'apply' as const,
            date: dateHelper.formattedUserFacingMonthDayLong(
                getRelativeDate(cohort, round.applicationDeadlineDaysOffset),
            ),
        };
    }, [currentUser, dateHelper]);

    const handleCtaClick = useCallback(() => {
        if (!cta) return;
        loadRoute(cta.type === 'register' ? '/settings/application_status' : '/settings/application');
    }, [cta, loadRoute]);

    return (
        <PartialAccessModalPresentation brandName={brandName} cta={cta} onClose={onClose} onCtaClick={handleCtaClick} />
    );
}
